/* ══ THE MEMO FORMAT — A READING, NOT A SYNTAX. PHASE 6.5 STEP 3.5 / 3.6. ═
 *
 * `memo` (→ `personal_note`, PRIVATE) is one free-text field the collector
 * owns. His rulings put two things in it and this file says how a reader
 * tells them apart WITHOUT the app claiming the field:
 *
 *   ruling 2, 2026-09-15   the EPQ/PPQ toggle "lives in memo … it is a
 *                          READ of the memo text, not a flag beside it"
 *   ruling 3, 2026-09-15   "write the SERVICE alone into memo by default.
 *                          Leave the number for typing." The cert.
 *   ruling,   2026-09-17   the holder's NUMBER is NOT here. It is the
 *                          custom grade's number, typed on the Custom chip
 *                          and carried in the grade key (catalog.js). This
 *                          REPLACES the 09-16 "PMG 66" ruling: memo carries
 *                          the service alone — "PMG".
 *
 * ── THE FORMAT ──────────────────────────────────────────────────────────
 *   the service   one all-caps token of 2–6 letters at the START of the
 *                 memo — "PMG", "PCGS", "CGA", "LEGACY". Read only there,
 *                 because there is the one place the app will ever write
 *                 it (3.6, into an EMPTY memo). A collector may type it
 *                 anywhere; the app makes no claim about it elsewhere.
 *                 ⚠ All caps on purpose: a memo that starts "serial …" or
 *                 "Bought at …" must NOT read as a service, and there is
 *                 no service list here to tell the two apart. The cost is
 *                 that "Legacy" in mixed case reads as no service — the
 *                 gate prints it as a `....` line, not a pass.
 *   the mark      the whole word EPQ or PPQ, ANYWHERE, any case. Whole
 *                 word means not glued to a letter on either side; digits
 *                 before it are fine ("65PPQ" is how a PCGS label gets
 *                 typed). The toggle is ON when it is present, OFF when it
 *                 is not — nothing else decides it.
 *   the rest      the collector's. Never rewritten by anything here.
 *
 * ── THE WRITES, AND THE PROPERTY THEY KEEP ──────────────────────────────
 *   withMark      inserts the mark once: after the service if the memo
 *                 starts with one, else at the end. A memo already carrying
 *                 a mark is returned byte-identical.
 *   withoutMark   removes every mark and the ONE space that joined it,
 *                 nothing else. A memo with no mark is returned
 *                 byte-identical, odd spacing and all.
 *   withHolder    writes the service into an EMPTY memo only (3.6, his
 *                 rule 2026-09-16: "never over anything the collector
 *                 typed") and refuses anything that is not the shape the
 *                 format reads back. Called by nothing yet — the source of
 *                 the string is a reading nobody has, and the write is
 *                 decision 4's confirmation step.
 *   so            withoutMark(withMark(t)) === t for any t with no mark —
 *                 the collector's text survives the toggle in both
 *                 directions. `grade-check` §6 measures it.
 *
 * ⚠ A collector who types the word turns the toggle on; one who deletes it
 * turns it off. His ruling 2026-09-16 on "not EPQ": "leave it. The word is
 * the state." That is what makes the state survive a sync with no column
 * of its own.
 *
 * ⚠ Written without template literals — string concatenation only — so the
 * twin's anchors carry no interpolation and `anchor-sweep` can check every
 * one of them. (Method rule 9: the first draft of this comment quoted the
 * two characters it avoids, and the gate's static line went red on it.)
 */

const MARKS=["EPQ","PPQ"];

/* The mark: a whole word, not glued to a letter on either side. */
const MARK_RE=/(?<![A-Za-z])(EPQ|PPQ)(?![A-Za-z])/i;

/* The service, where the app writes it: the start, one all-caps token,
 * not glued to a letter or digit, and NOT one of the two marks — a memo
 * that is "EPQ serial …" has no service, it has the toggle on. "PMG66"
 * does not read; "PMG 66" reads PMG and the 66 is the collector's text. */
const HOLDER_RE=/^\s*(?!(?:EPQ|PPQ)(?![A-Za-z0-9]))([A-Z]{2,6})(?![A-Za-z0-9])/;
const SERVICE_RE=/^(?!EPQ$|PPQ$)[A-Z]{2,6}$/;

/** "EPQ" | "PPQ" | null — the toggle's state, read off the text. */
function readMark(text){const m=MARK_RE.exec(String(text||""));return m?m[1].toUpperCase():null;}

/** {service, text} | null — the service at the start, or nothing. */
function readHolder(text){const m=HOLDER_RE.exec(String(text||""));return m?{service:m[1],text:m[1]}:null;}

/** Which word ON writes. His ruling, 2026-09-16, verbatim: "use the service
 * table — PMG writes EPQ, any other service writes PPQ, no holder writes
 * EPQ. Two entries, both facts about how those services label, not
 * aliases. That is a different thing from the scorer's table and I accept
 * it." The service is read off the start of the memo. */
function markFor(text){const h=readHolder(text);if(!h)return "EPQ";return h.service.toUpperCase()==="PMG"?"EPQ":"PPQ";}

/** The memo with the service written — into an EMPTY memo only. */
function withHolder(memo,service){
  const t=String(memo||"");
  if(t.trim()!=="")return t;
  const s=String(service||"").trim();
  if(!SERVICE_RE.test(s))return t;
  return s;
}

/** The memo with the mark present, written once. */
function withMark(text,mark){
  const t=String(text||"");
  if(readMark(t))return t;
  const h=HOLDER_RE.exec(t);
  if(h){const at=h[0].length;return t.slice(0,at)+" "+mark+t.slice(at);}
  return t===""?mark:t+" "+mark;
}

/** The memo with every mark gone, and only the space that joined each one. */
function withoutMark(text){
  let t=String(text||"");
  for(;;){
    const m=MARK_RE.exec(t);if(!m)return t;
    let a=m.index,b=a+m[0].length;
    if(a>0&&t[a-1]===" ")a--;
    else if(t[b]===" ")b++;
    t=t.slice(0,a)+t.slice(b);
  }
}

/* ══ THE QUALIFIER MOVED OUT OF THIS FIELD — his ruling 2026-09-21 ════
 *
 * ⚠⚠⚠ `qualifier` IS NOW ITS OWN COLUMN (db/012) AND THE MEMO NO LONGER
 * CARRIES EPQ/PPQ. His reason: the memo is free text he owns, and the
 * letters in it were duplicative. The two functions below are the whole
 * of the transition, and BOTH ARE MEANT TO BE DELETED.
 */

/**
 * ⏸⏸ THE COMPATIBILITY READ — HIS RULING 2, 2026-09-21, FOR **ONE
 * RELEASE**, AND HERE IS HOW IT GETS RETIRED.
 *
 * *"The toggle is ON if the new field is on OR the memo still carries
 * the mark."*
 *
 * ⚠ WHY IT IS NEEDED AT ALL: the carry-over runs in the CLIENT, once,
 * per device. Between this shipping and a given device running it, that
 * device has the letters in `memo` and `qualifier = false` — so without
 * this read **a collector's EPQ would silently turn OFF** until they
 * next opened the app.
 *
 * ▶ HOW TO RETIRE IT, so it is removed deliberately rather than left:
 *   1. The carry-over below sets `qualifier` and strips the letters, and
 *      a save syncs both. **The condition to retire on is that no row
 *      in `holdings` has a `personal_note` matching the mark** — one
 *      read with the service key answers it:
 *        select count(*) from holdings where personal_note ~* '(^|[^a-z])(epq|ppq)([^a-z]|$)';
 *   2. When that count is 0, delete THIS function and `carryOverMark`
 *      below, change `qualifierOn(h)` at its call sites to `!!h.qualifier`,
 *      and drop `readMark`/`withoutMark` if nothing else calls them.
 *   3. ⚠ It cannot be retired on a DATE, because it is a fact about
 *      collectors' rows and not about the calendar.
 * ⚠⚠ Until then this is the ONLY thing that may read the toggle's state,
 * and `grade-check` asserts the call sites so a second reading cannot
 * quietly appear beside it.
 */
function qualifierOn(h){return !!(h&&(h.qualifier||readMark(h.memo)));}

/**
 * ⏸ THE CARRY-OVER — HIS RULING 1, 2026-09-21: **in the client, once,
 * reusing the proven removal function — never a second copy of the mark
 * regex in SQL.** Two implementations of one predicate is how one drifts.
 *
 * *"Any memo with EPQ or PPQ gets the toggle ON and the letters
 * removed."*
 *
 * ⚠⚠ IT REWRITES THE COLLECTOR'S FREE TEXT, which is the one field this
 * app has always promised not to own — so it uses `withoutMark`, which
 * takes the mark and ONE joining space and nothing else, and is measured
 * byte-reversible on nine collector texts in `grade-check` §6.
 *
 * ⚠ IDEMPOTENT BY SHAPE, not by a flag: after it runs there is no mark
 * left to find, so a second run changes nothing. **There is no "has
 * migrated" marker to store and none to forget** — which is what keeps
 * it out of storage entirely, per his memory-only standing rule.
 *
 * Returns the number of holdings it changed, so a caller can decide
 * whether a save is owed rather than saving unconditionally.
 */
function carryOverMark(collection){
  let n=0;
  for(const h of (collection||[])){
    if(!readMark(h.memo))continue;
    h.qualifier=true;
    h.memo=withoutMark(h.memo);
    n++;
  }
  return n;
}

export {MARKS,carryOverMark,markFor,qualifierOn,readMark,readHolder,withHolder,withMark,withoutMark};
